import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, X, Star, ShieldCheck } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { authFetch } from "../context/AuthContext";
import { toast } from "sonner";

const SIGN_GLYPH = {
    Aries: "♈", Taurus: "♉", Gemini: "♊", Cancer: "♋", Leo: "♌", Virgo: "♍",
    Libra: "♎", Scorpio: "♏", Sagittarius: "♐", Capricorn: "♑", Aquarius: "♒", Pisces: "♓",
};

const VERIFIED_PREF_KEY = "soulstar_verified_only";

export default function Matches() {
    const [matches, setMatches] = useState([]);
    const [idx, setIdx] = useState(0);
    const [loading, setLoading] = useState(true);
    const [verifiedOnly, setVerifiedOnly] = useState(
        () => localStorage.getItem(VERIFIED_PREF_KEY) === "true",
    );

    useEffect(() => {
        load(verifiedOnly);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verifiedOnly]);

    const load = async (v) => {
        setLoading(true);
        try {
            const res = await authFetch("get", `/matches/feed?verified_only=${v ? "true" : "false"}`);
            setMatches(res.data.matches);
            setIdx(0);
        } catch {
            toast.error("Failed to load matches");
        } finally {
            setLoading(false);
        }
    };

    const toggleVerifiedOnly = () => {
        const next = !verifiedOnly;
        setVerifiedOnly(next);
        localStorage.setItem(VERIFIED_PREF_KEY, String(next));
    };

    const swipe = async (action) => {
        const current = matches[idx];
        if (!current) return;
        try {
            const res = await authFetch("post", "/matches/swipe", {
                target_user_id: current.user_id, action,
            });
            if (res.data.matched) {
                toast.success(`✦ Cosmic match with ${current.name}!`);
            }
        } catch {}
        setIdx(idx + 1);
    };

    const current = matches[idx];
    const score = current?.compatibility?.overall || 0;

    return (
        <>
            <Navbar />
            <main className="min-h-screen pt-28 pb-12 px-6">
                <div className="max-w-2xl mx-auto">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                        <div>
                            <p className="label-tracking text-gold">Cosmic Connections</p>
                            <h1 className="font-serif text-4xl mt-2">Your Constellation</h1>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={toggleVerifiedOnly}
                                className={`inline-flex items-center gap-2 text-xs label-tracking rounded-full px-3 py-2 border transition-colors ${
                                    verifiedOnly
                                        ? "bg-gold/15 border-gold/50 text-gold"
                                        : "bg-white/5 border-white/10 text-muted hover:border-gold/30 hover:text-gold"
                                }`}
                                data-testid="verified-only-toggle"
                                aria-pressed={verifiedOnly}
                                title={verifiedOnly ? "Showing verified only" : "Show verified only"}
                            >
                                <ShieldCheck size={14} weight={verifiedOnly ? "fill" : "duotone"} />
                                Verified only
                            </button>
                            <Link to="/my-matches" className="btn-gold" data-testid="my-matches-link">Matches</Link>
                        </div>
                    </div>

                    {loading && <p className="text-muted text-center py-20">Aligning the stars...</p>}

                    {!loading && !current && (
                        <div className="glass p-12 text-center">
                            <p className="font-serif text-3xl">
                                {verifiedOnly ? "No verified souls tonight" : "The stars rest tonight"}
                            </p>
                            <p className="text-muted mt-3">
                                {verifiedOnly
                                    ? "Turn off Verified-only to widen your view, or return when new profiles are checked."
                                    : "No new souls to meet. Return when the constellations shift."}
                            </p>
                            <button onClick={() => { setIdx(0); load(verifiedOnly); }} className="btn-gold mt-6" data-testid="refresh-matches">
                                Cast Again
                            </button>
                        </div>
                    )}

                    {current && (
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={current.user_id}
                                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, x: -100 }}
                                transition={{ duration: 0.4 }}
                                className="glass-gold p-8"
                                data-testid="match-card"
                            >
                                {/* Score Ring */}
                                <div className="flex justify-center -mt-2 mb-6">
                                    <div className="relative w-24 h-24">
                                        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                                            <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
                                            <circle cx="50" cy="50" r="44" fill="none" stroke="#E6C387" strokeWidth="4"
                                                strokeDasharray={`${score * 2.76} 999`} strokeLinecap="round" />
                                        </svg>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="font-serif text-3xl text-gold" data-testid="match-score">{score}</span>
                                        </div>
                                    </div>
                                </div>

                                <h2 className="font-serif text-4xl text-center" data-testid="match-name">{current.name}</h2>
                                <p className="text-center text-muted mt-1 text-sm">{current.birth_location}</p>

                                <div className="grid grid-cols-3 gap-3 mt-6">
                                    <SignBox label="Sun" sign={current.sun} />
                                    <SignBox label="Moon" sign={current.moon} />
                                    <SignBox label="Rising" sign={current.rising} />
                                </div>

                                {current.bio && (
                                    <p className="text-muted text-sm italic font-serif mt-6 text-center leading-relaxed">"{current.bio}"</p>
                                )}

                                {/* Compat breakdown */}
                                <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
                                    <p className="label-tracking text-gold text-center">Cosmic Reading</p>
                                    <p className="text-sm text-center mt-3 font-serif italic">
                                        {current.compatibility.sun_compat.description}
                                    </p>
                                    <p className="text-xs text-muted text-center mt-2">
                                        {current.compatibility.sun_compat.aspect}
                                    </p>
                                </div>

                                <div className="flex justify-center gap-6 mt-8">
                                    <button
                                        onClick={() => swipe("pass")}
                                        className="w-16 h-16 rounded-full border border-white/15 flex items-center justify-center hover:border-white/30 hover:bg-white/5 transition-all"
                                        data-testid="swipe-pass"
                                        aria-label="Pass"
                                    >
                                        <X size={24} className="text-muted" />
                                    </button>
                                    <button
                                        onClick={() => swipe("like")}
                                        className="w-16 h-16 rounded-full bg-gradient-to-br from-[#E6C387] to-[#F5D68F] flex items-center justify-center hover:scale-110 transition-transform"
                                        data-testid="swipe-like"
                                        aria-label="Like"
                                    >
                                        <Heart size={24} weight="fill" className="text-[#03030A]" />
                                    </button>
                                    <Link
                                        to={`/compatibility/${current.user_id}`}
                                        className="w-16 h-16 rounded-full border border-gold/30 flex items-center justify-center hover:bg-gold/10 transition-all"
                                        data-testid="view-compatibility"
                                        aria-label="View detailed compatibility"
                                    >
                                        <Star size={22} className="text-gold" />
                                    </Link>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    )}
                </div>
            </main>
        </>
    );
}

function SignBox({ label, sign }) {
    return (
        <div className="text-center p-3 rounded-xl bg-white/5 border border-white/10">
            <p className="label-tracking text-xs">{label}</p>
            <p className="font-serif text-2xl text-gold mt-1">{SIGN_GLYPH[sign]}</p>
            <p className="text-xs mt-1">{sign}</p>
        </div>
    );
}
